import { z } from 'zod';
import { DataRule } from 'libs/end-to-end/src/data-rules';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { HiringCopilotDropdowns } from 'libs/end-to-end/src/custom-viz/HiringCopilotTypes';
import { HiringCopilotRequest } from 'libs/end-to-end/src/custom-viz/HiringCopilotTypes';
import { PromotionCopilotDropdowns } from 'libs/end-to-end/src/custom-viz/PromotionCopilotTypes';
import { PromotionCopilotRequest } from 'libs/end-to-end/src/custom-viz/PromotionCopilotTypes';
import { SuccessPhenotypeV2Request } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeTypes';

/**
 * Model-specific schema for the hiring copilot branch of the copilot model
 * experimentation state machine. The UI sends full dropdowns (department,
 * job_title, location, start_date, end_date); the state machine builds the
 * HiringCopilotRequest directly without calling get-hiring-copilot-request-inputs.
 */
export const HiringCopilotExperimentationModelInputSchema = z.object({
  modelType: z.literal('hiring-copilot'),
  dropdowns: HiringCopilotDropdowns,
});

/**
 * Model-specific schema for the promotion copilot branch of the copilot model
 * experimentation state machine. The UI sends dropdowns (department, job_title,
 * location); the API builds the PromotionCopilotRequest, same as hiring-copilot.
 */
export const PromotionCopilotExperimentationModelInputSchema = z.object({
  modelType: z.literal('promotion-copilot'),
  dropdowns: PromotionCopilotDropdowns,
});

/**
 * Model-specific schema for the key factors (success factors) branch of the
 * copilot model experimentation state machine.
 */
export const KeyFactorsExperimentationModelInputSchema = z.object({
  modelType: z.literal('key-factors'),
  metric: z.string(),
  filters: z.array(FilterDefinitionForExtras).default([]),
});

/**
 * Discriminated union schema of model types for copilot model experimentation.
 */
export const CopilotModelExperimentationModelInputSchema = z.discriminatedUnion(
  'modelType',
  [
    HiringCopilotExperimentationModelInputSchema,
    PromotionCopilotExperimentationModelInputSchema,
    KeyFactorsExperimentationModelInputSchema,
  ],
);

/**
 * Base input schema shared by all copilot model experimentation runs.
 */
export const CopilotModelExperimentationBaseInputSchema = z.object({
  organizationId: z.string(),
  databaseId: z.string(),
  revisionId: z.string(),
  dataRules: z.array(DataRule),
  rootHash: z.string(),
});

/**
 * Pre-built custom viz request for hiring-copilot, key-factors, or promotion-copilot.
 * Built in the API layer and passed through so the state machine does not
 * construct it (avoids interpreter stripping empty arrays from Parameters).
 */
export const CopilotModelExperimentationCustomVizRequestSchema = z.union([
  HiringCopilotRequest,
  SuccessPhenotypeV2Request,
  PromotionCopilotRequest,
]);

export type CopilotModelExperimentationCustomVizRequest = z.infer<
  typeof CopilotModelExperimentationCustomVizRequestSchema
>;

/**
 * Full input schema for the copilot model experimentation state machine.
 * When modelType is hiring-copilot, key-factors, or promotion-copilot, the API
 * sends a pre-built customVizRequest; the state machine passes it through.
 */
export const CopilotModelExperimentationStateMachineInputSchema =
  CopilotModelExperimentationBaseInputSchema.merge(
    z.object({
      model: CopilotModelExperimentationModelInputSchema,
      customVizRequest: CopilotModelExperimentationCustomVizRequestSchema.optional(),
    }),
  );

export type HiringCopilotExperimentationModelInput = z.infer<
  typeof HiringCopilotExperimentationModelInputSchema
>;
export type PromotionCopilotExperimentationModelInput = z.infer<
  typeof PromotionCopilotExperimentationModelInputSchema
>;
export type KeyFactorsExperimentationModelInput = z.infer<
  typeof KeyFactorsExperimentationModelInputSchema
>;
export type CopilotModelExperimentationModelInput = z.infer<
  typeof CopilotModelExperimentationModelInputSchema
>;
export type CopilotModelExperimentationBaseInput = z.infer<
  typeof CopilotModelExperimentationBaseInputSchema
>;
export type CopilotModelExperimentationStateMachineInput = z.infer<
  typeof CopilotModelExperimentationStateMachineInputSchema
>;

// ---------------------------------------------------------------------------
// API request body schemas
// ---------------------------------------------------------------------------

/**
 * API request body schema for the hiring copilot variant.
 * The client sends the full dropdowns (built from filter selections and date range).
 */
export const HiringCopilotExperimentationRequestSchema = z.object({
  modelType: z.literal('hiring-copilot'),
  dropdowns: HiringCopilotDropdowns,
});

/**
 * API request body schema for the key factors variant.
 * Applies a default of [] for `filters` so the field is optional in the request.
 */
export const KeyFactorsExperimentationRequestSchema = z.object({
  modelType: z.literal('key-factors'),
  metric: z.string(),
  filters: z.array(FilterDefinitionForExtras).default([]),
});

/**
 * API request body schema for the promotion copilot variant.
 * The client sends dropdowns (department, job_title, location), same as hiring.
 */
export const PromotionCopilotExperimentationRequestSchema = z.object({
  modelType: z.literal('promotion-copilot'),
  dropdowns: PromotionCopilotDropdowns,
});

/**
 * Discriminated union schema for the copilot model experimentation API
 * request body. Hiring-copilot and promotion-copilot require dropdowns;
 * key-factors has `filters` (defaults to [] when omitted).
 */
export const RunCopilotModelExperimentationRequestBodySchema =
  z.discriminatedUnion('modelType', [
    HiringCopilotExperimentationRequestSchema,
    PromotionCopilotExperimentationRequestSchema,
    KeyFactorsExperimentationRequestSchema,
  ]);

export type RunCopilotModelExperimentationRequestBodySchema = z.infer<
  typeof RunCopilotModelExperimentationRequestBodySchema
>;
